import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Download } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { Loader } from "@/components/brand";
import { Button } from "@/components/ui/button";

export default function TakeCourse() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [step, setStep] = useState("learn");
  const [answers, setAnswers] = useState({});
  const [ack, setAck] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    api.get(`/training/assignments/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!d) return <Loader />;
  const { course, assignment } = d;
  const submit = async () => {
    try {
      const { data } = await api.post(`/training/assignments/${id}/submit`, { answers: course.quiz.map((_, i) => answers[i] ?? -1), acknowledge: ack });
      setResult(data);
      setStep("result");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="take-course">
      <Link to="/app/training" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />My training</Link>
      <div><p className="eyebrow">{course.duration_minutes} minute course · pass mark {course.pass_mark}%</p><h1 className="mt-2 text-3xl font-bold">{course.title}</h1><p className="mt-2 text-muted-foreground">{course.description}</p></div>
      {assignment.status === "completed" && step !== "result" && <div className="rounded-xl border border-health/30 bg-health/10 p-4 text-sm">You completed this course with {assignment.score}%. Valid until {assignment.expires_at}.</div>}
      {step === "learn" && (
        <>
          {course.objectives?.length > 0 && <div className="rounded-2xl border bg-card p-6"><h2 className="font-semibold">Learning objectives</h2><ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{course.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul></div>}
          {course.sections.map((s, i) => <article key={i} className="rounded-2xl border bg-card p-6"><h2 className="font-semibold">{s.heading}</h2><div className="doc-prose mt-3 text-sm">{s.content.split("\n\n").map((p, k) => <p key={k} className="whitespace-pre-line">{p}</p>)}</div></article>)}
          {assignment.status !== "completed" && <Button className="w-full" size="lg" onClick={() => setStep("quiz")} data-testid="go-to-quiz">I've read the material — take the quiz</Button>}
        </>
      )}
      {step === "quiz" && (
        <div className="space-y-4">
          {course.quiz.map((q, i) => (
            <div key={q.id} className="rounded-2xl border bg-card p-5"><p className="font-medium">{i + 1}. {q.question}</p>
              <div className="mt-3 space-y-2">{q.options.map((o, j) => <button key={j} onClick={() => setAnswers({ ...answers, [i]: j })} data-testid={`answer-${i}-${j}`} className={`block w-full rounded-xl border px-4 py-2.5 text-left text-sm transition-colors ${answers[i] === j ? "border-health bg-health/10" : "hover:bg-accent"}`}>{o}</button>)}</div></div>
          ))}
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="training-ack" />I confirm I have read and understood this training.</label>
          <Button className="w-full" size="lg" disabled={Object.keys(answers).length < course.quiz.length || !ack} onClick={submit} data-testid="submit-quiz">Submit answers</Button>
        </div>
      )}
      {step === "result" && result && (
        <div className="rounded-2xl border bg-card p-8 text-center" data-testid="quiz-result">
          {result.passed ? <CheckCircle2 className="mx-auto h-12 w-12 text-health" /> : <XCircle className="mx-auto h-12 w-12 text-alert" />}
          <h2 className="mt-4 text-2xl font-bold">{result.passed ? "Passed" : "Not passed yet"} — {result.score}%</h2>
          <p className="mt-1 text-sm text-muted-foreground">{result.correct} of {result.total} correct · pass mark {result.pass_mark}%</p>
          {result.passed ? <Button className="mt-6" onClick={() => download(`/training/assignments/${id}/certificate`, "training-record.pdf")} data-testid="download-record"><Download className="mr-1.5 h-4 w-4" />Download completion record</Button>
            : <Button className="mt-6" onClick={() => { setStep("learn"); setAnswers({}); }} data-testid="retry-course">Review and try again</Button>}
        </div>
      )}
    </div>
  );
}
